import {
  ButtonProps,
  Link,
  ListItem,
  Text,
  UnorderedList,
  useToast,
} from "@chakra-ui/react";
import React, { Fragment, useState } from "react";
import ShiButton from "src/atoms/ShiButton";
import useMigration from "src/hooks/useMigration";
import MigrationModal from "./MigrationModal";
import { getMigrationProductName } from "src/lib/getMigrationProductName";
import { useLocation } from "@reach/router";

type MigrationButtonProps = ButtonProps & {
  apiEndpoint: string;
};

const getMigrationErrorToast = (productName?: string, status?: number) => {
  const baseToastOptions = {
    status: "error" as const,
    duration: 12000,
    isClosable: true,
  };

  switch (status) {
    case 400:
      return {
        ...baseToastOptions,
        // title: "Erreur 400",
        description: `La migration n'est pas encore disponible pour votre produit. Veuillez patienter et réessayer lorsque vous aurez reçu une communication de Cegid vous invitant à évoluer.`,
      };
    case 401:
      return {
        ...baseToastOptions,
        // title: "Erreur 401",
        description: (
          <Fragment>
            <Text>
              Votre session a expiré. Veuillez retourner sur votre produit{" "}
              {productName} et cliquer à nouveau sur le bouton pour relancer le
              processus de migration.
            </Text>
            <Text mt="space-8">
              Si le problème persiste, contactez notre support:{" "}
              <Link href="mailto:support@shine.co">support@shine.co</Link>
            </Text>
          </Fragment>
        ),
      };
    case 409:
      return {
        ...baseToastOptions,
        title:
          "Votre adresse email est déjà associée à un compte Shine Facture.",
        description: (
          <>
            <Text>Plusieurs situations sont possibles:</Text>
            <UnorderedList mt="space-8" spacing="space-8" pl="space-8">
              <ListItem>
                <Text as="span" fontWeight="bold">
                  Votre compte a déjà été migré:
                </Text>{" "}
                vous n&apos;avez rien de plus à faire, connectez-vous
                directement à Shine Facture.
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">
                  Vous possédez plusieurs entreprises:
                </Text>{" "}
                la gestion multi-entreprises arrive très prochainement et vous
                pourrez bientôt migrer toutes vos entreprises vers Shine
                Facture.
              </ListItem>
              <ListItem>
                <Text as="span" fontWeight="bold">
                  Vous avez déjà ouvert un compte Shine Facture avant de faire
                  la migration:
                </Text>{" "}
                contactez notre support pour bénéficier de la migration:{" "}
                <Link href="mailto:support@shine.co">support@shine.co</Link>
              </ListItem>
            </UnorderedList>
          </>
        ),
      };
    case 500:
      return {
        ...baseToastOptions,
        // title: "Erreur 500",
        description: `Une erreur inattendue s'est produite. Veuillez réessayer en quittant cette page et en cliquant à nouveau sur le bouton de migration depuis votre produit ${productName}.`,
      };
    default:
      return {
        ...baseToastOptions,
        title: "Un problème est survenu",
        description:
          "La migration n'a pas pu démarrer. Veuillez contacter le support client.",
      };
  }
};

const MigrationButton = ({
  apiEndpoint,
  children,
  ...props
}: MigrationButtonProps) => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { triggerMigration, hasMigrationToken, isLoading } = useMigration();
  const productName = getMigrationProductName(pathname) ?? "";
  const toast = useToast();

  const handleConfirm = async () => {
    const res = await triggerMigration(apiEndpoint);

    if (res?.ok) {
      setIsSuccess(true);
    } else {
      toast(getMigrationErrorToast(productName, res?.status));
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSuccess(false);
  };

  return (
    <Fragment>
      <MigrationModal
        isOpen={isOpen}
        isSuccess={isSuccess}
        isLoading={isLoading}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <ShiButton
        hasArrow={false}
        onClick={() => setIsOpen(true)}
        isDisabled={!hasMigrationToken}
        sx={{
          "&[disabled], &[aria-disabled=true]": {
            "&:hover": { background: "btn-green-bg" },
          },
        }}
        {...(props as ButtonProps)}
      >
        {children}
      </ShiButton>
    </Fragment>
  );
};

export default MigrationButton;
